import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import { MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import $ from 'jquery';
import industries from "@/models/optionsIndustries";
import { searchEntities, type BusinessEntity } from "@/components/api/nzbnApi";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import cities from "@/models/optionsCities";


// Complete contact form schema
const contactFormSchema = z.object({
    searchTerm: z.string().optional(),
    companyName: z.string().min(3, { message: "Company name is required" }),
    NZBN: z.string().optional(),
    industry: z.number().int().positive({ message: "Industry is required" }),
    firstName: z.string().min(3, { message: "First name is required" }),
    lastName: z.string().min(3, { message: "Last name is required" }),
    email: z.string().email().min(3, { message: "Email is required" }),
    phone: z.string().min(3, { message: "Phone number is required" }),
    street: z.string().optional(),
    building: z.string().optional(),
    suburb: z.string().optional(),
    city: z.string().optional(),
    postalCode: z.string()
        .regex(/^\d{4}$/, { message: "Postcode must be 4 digits" })
        .min(4, { message: "Postcode is required" }),
    // postalCode: z.string().min(3, { message: "Postal Code is required" }),
    country: z.string().min(3, { message: "Country is required" }),
    subject: z.string().min(3, { message: "Subject is required" }),
    message: z.string().min(10, { message: "Message is required (minimum 10 characters)" }),
    files: z.array(z.instanceof(File)).max(3, { message: "Maximum 3 files allowed" }).optional(),
    submissionTime: z.string().default(new Date().toISOString()),
    // Enhanced honeypot fields
    honeypot: z.string().optional(), // Traditional honeypot
    website: z.string().optional(), // Fake website field
    confirmEmail: z.string().optional(), // Fake email confirmation
    botCheck: z.string().optional(), // Hidden bot check
    formToken: z.string().default(() => btoa(`${Date.now()}-${Math.random()}`)), // Form token
    formStartTime: z.number().default(Date.now), // Form start time
});

type ContactForm = z.infer<typeof contactFormSchema>;

interface ContactFormSheetProps {
    children?: React.ReactNode;
    buttonText?: React.ReactNode;
    icon?: React.ReactNode;
    buttonClassName?: string;
    open?: boolean;           // new
    onOpenChange?: (open: boolean) => void;  // new
    hideTrigger?: boolean;    // new - hide button when triggered externally
}

export default function ContactFormSheet({
    children,
    buttonText = "Contact Us Now",
    icon,
    buttonClassName = "px-4 sm:px-8 py-2 sm:py-3 text-sm sm:text-lg",
    open,
    onOpenChange,
    hideTrigger = false,
}: ContactFormSheetProps) {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [businessEntities, setBusinessEntities] = useState([] as BusinessEntity[]);
    const [selectedBusinessEntity, setSelectedBusinessEntity] = useState({} as BusinessEntity);
    const [formStartTime] = useState(Date.now()); // Track when form was loaded
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    // Additional ResizeObserver error suppression for this component
    useEffect(() => {
        const handleError = (e: ErrorEvent) => {
            if (e.message?.includes('ResizeObserver')) {
                e.preventDefault();
                e.stopImmediatePropagation();
                return false;
            }
        };

        window.addEventListener('error', handleError, true);
        return () => {
            window.removeEventListener('error', handleError, true);
        };
    }, []);

    const { register, handleSubmit, formState: { errors, isValid }, setValue, reset } = useForm<ContactForm>({
        resolver: zodResolver(contactFormSchema),
        mode: 'onChange'
    });

    const [submitting, setSubmitting] = useState(false);

    const handleBusinessEntitySearch = async (searchTerm: string) => {
        if (!searchTerm) {
            setBusinessEntities([]);
            return;
        }
        try {
            const res = await searchEntities(searchTerm, 'businessName');
            setBusinessEntities(res);
        } catch (error) {
            console.error("Error fetching business entities:", error);
        }
    };

    const handleBusinessEntityClick = async (entity: BusinessEntity) => {
        setSelectedBusinessEntity(entity);
        setValue("companyName", entity.label);
        setValue("NZBN", entity.info.nzbn);
        setValue("searchTerm", '');
        setBusinessEntities([]);
    };

    const handleFormReset = () => {
        reset();
        setSelectedBusinessEntity({} as BusinessEntity);
        setBusinessEntities([]);
        setSelectedFiles([]);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (files.length > 3) {
            alert("Maximum 3 files allowed");
            return;
        }
        setSelectedFiles(files);
        setValue("files", files);
    };

    const removeFile = (indexToRemove: number) => {
        const updatedFiles = selectedFiles.filter((_, index) => index !== indexToRemove);
        setSelectedFiles(updatedFiles);
        setValue("files", updatedFiles);
    };

    const handleFormSubmit = async (data: ContactForm) => {
        setSubmitting(true); //added for spinning
        // Enhanced bot detection
        const submitTime = Date.now();
        const formFillTime = submitTime - formStartTime;

        // Bot detection checks
        if (data.honeypot || data.website || data.confirmEmail || data.botCheck) {
            setSubmitting(false); //added for loading
            console.log("Bot detected: Honeypot fields filled");
            return;
        }

        // Check if form was submitted too quickly (likely a bot)
        if (formFillTime < 3000) { // Less than 3 seconds
            setSubmitting(false); //added for loading
            console.log("Bot detected: Form submitted too quickly");
            return;
        }

        // Check if form was submitted too slowly (likely abandoned/bot)
        if (formFillTime > 1800000) { // More than 30 minutes
            console.log("Form session expired");
            return;
        }

        // Validate form token
        try {
            const tokenData = atob(data.formToken);
            const [tokenTime] = tokenData.split('-');
            const tokenAge = submitTime - parseInt(tokenTime);

            if (tokenAge > 1800000) { // Token older than 30 minutes
                console.log("Form token expired");
                return;
            }
        } catch (error) {
            console.log("Invalid form token");
            return;
        }

        // Convert files to base64 for submission
        const fileData = await Promise.all(
            (data.files || []).map(async (file) => {
                return new Promise<{ name: string, size: number, type: string, data: string }>((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        resolve({
                            name: file.name,
                            size: file.size,
                            type: file.type,
                            data: reader.result as string
                        });
                    };
                    reader.readAsDataURL(file);
                });
            })
        );

        // [12-May-2026] Marketing Attribution: Read referrer, gclid, campaign ID
        const referrer = sessionStorage.getItem('original_referrer') || '';
        let gadsCampaignId = '';
        let gclid = '';
        try {
            const stored = localStorage.getItem('gads_attribution');
            if (stored) {
                const parsed = JSON.parse(stored);
                const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
                if (Date.now() - parsed.capturedAt < thirtyDaysMs) {
                    gadsCampaignId = parsed.gad_campaignid || '';
                    gclid = parsed.gclid || '';
                } else {
                    localStorage.removeItem('gads_attribution');
                }
            }
        } catch { /* ignore parse errors */ }

        // Create submission data without the File objects
        const submissionData = {
            ...data,
            files: fileData, // Replace File objects with serializable data
            submissionTime: new Date().toISOString(),
            referrer,
            gadsCampaignId,
            gclid,
        };

        const token = sessionStorage.getItem("x-token");

        const safeAjax = (ajaxOptions: any) => {
            const deferredAjax = $.Deferred();
            if (!ajaxOptions.headers) {
                ajaxOptions.headers = {
                    "__RequestVerificationToken": token
                };
            } else {
                ajaxOptions.headers["__RequestVerificationToken"] = token;
            }

            $.ajax(ajaxOptions)
                .done(deferredAjax.resolve)
                .fail(deferredAjax.reject);

            return deferredAjax.promise();
        };
        // new flow url with full address including postcode: /_api/cloudflow/v1.0/trigger/261cfac2-f60d-f111-8406-6045bde5bdaf
        // old flow url (with country only): /_api/cloudflow/v1.0/trigger/e87c1674-5c04-5eb5-7d65-dd5c64a58e33
        //12/5/26: use the old flow url to test the new 3 fields (gadsCampaignId, gclid, referrer) without affecting the new flow which is currently in use on the website. Once testing is done, we can switch to the new flow url and remove the old one.

        try {
            await safeAjax({
                type: "POST",
                url: `${process.env.REACT_APP_BASE_URL}/_api/cloudflow/v1.0/trigger/e87c1674-5c04-5eb5-7d65-dd5c64a58e33`,
                contentType: "application/json",
                data: JSON.stringify({ "eventData": JSON.stringify({ "payload": JSON.stringify(submissionData) }) }),
                processData: false,
                global: false,
                success: function (res: any, status: any, xhr: any) {
                    console.log("Response:", res);
                    console.log("entityID:", xhr.getResponseHeader("entityid"));
                    setIsSubmitted(true);
                    setSubmitting(false); //added for loading
                },
                error: function (xhr: any, status: any, error: any) {
                    setSubmitting(false); //added for loading
                    console.error("Error:", status, error);
                }
            });
        } catch (ex) {
            setSubmitting(false); //added for loading
            console.log(ex);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            {/* <SheetTrigger asChild>
                {children ? children : (
                    <Button variant={'red'} className={buttonClassName}>
                        {icon}
                        {buttonText}
                    </Button>
                )}
            </SheetTrigger> */}
            {!hideTrigger && (
                <SheetTrigger asChild>
                    {children ? children : (
                        <Button variant={'red'} className={buttonClassName}>
                            {icon}
                            {buttonText}
                        </Button>
                    )}
                </SheetTrigger>
            )}

            <SheetContent className="w-full max-w-full sm:max-w-lg md:max-w-2xl lg:max-w-3xl overflow-y-auto p-3 sm:p-6 
            bg-[#FAFBFC] md:rounded-lg shadow-2xl border border-gray-200 [&>button]:text-black [&>button]:ring-offset-[#FAFBFC] [&>button:hover]:text-black
           ipadpro-portrait:max-h-[80vh] ipadpro-portrait:overflow-y-auto" >
                <SheetHeader className="pb-3 sm:pb-4">
                    <SheetTitle className="text-base sm:text-xl md:text-2xl font-bold text-gradient-red">Get In Touch</SheetTitle>
                    {!isSubmitted && (
                        <SheetDescription className="text-xs sm:text-sm md:text-base text-gray-700">
                            Fill out the form below and we'll get back to you as soon as possible.
                        </SheetDescription>
                    )}
                </SheetHeader>

                {isSubmitted ? (
                    <div className="flex flex-col items-center justify-center space-y-3 sm:space-y-4 py-6 sm:py-8">
                        <MailCheck size={48} className="text-black sm:w-16 sm:h-16" />
                        <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-center text-black">Thank You!</h3>
                        <p className="text-center text-xs sm:text-sm md:text-base text-gray-700 px-2">
                            We've received your message and will get back to you shortly.
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-3 sm:space-y-4 py-2 sm:py-4">
                        {/* Enhanced Honeypot fields for bot protection */}
                        <div className="absolute -left-[9999px] invisible opacity-0 h-0 overflow-hidden">
                            <Input type="text" {...register("honeypot")} tabIndex={-1} autoComplete="nope" aria-hidden="true" />
                            <Input type="url" {...register("website")} placeholder="Website" tabIndex={-1} autoComplete="nope" aria-hidden="true" />
                            <Input type="email" {...register("confirmEmail")} placeholder="Confirm Email" tabIndex={-1} autoComplete="nope" aria-hidden="true" />
                            <Input type="text" {...register("botCheck")} tabIndex={-1} autoComplete="nope" aria-hidden="true" />
                            <input type="hidden" {...register("formToken")} />
                            <input type="hidden" {...register("formStartTime", { value: formStartTime })} />
                        </div>

                        {/* Company Information Section */}
                        <div className="border-b border-gray-200 pb-3 sm:pb-4">
                            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-black mb-2 sm:mb-3">Company Information</h3>

                            {/* Business Search */}
                            <div className="space-y-2">
                                <Label className="block text-xs sm:text-sm font-medium text-black">
                                    Search for your business by name or NZBN
                                </Label>
                                <div className="relative">
                                    <Input
                                        type="text"
                                        className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                        placeholder="Enter business name or NZBN..."
                                        autoComplete="off"
                                        {...register("searchTerm")}
                                        onChange={(e) => handleBusinessEntitySearch(e.target.value)}
                                    />

                                    {/* Business Entity Results - Dynamic height for mobile */}
                                    {businessEntities.length > 0 && (
                                        <div className="absolute top-full left-0 right-0 z-50 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 sm:max-h-48 overflow-y-auto mt-1">
                                            {businessEntities.filter((item: BusinessEntity) => item.info.status === 'Registered').map((entity: BusinessEntity) => (
                                                <div
                                                    key={entity.value}
                                                    className="p-2 sm:p-3 cursor-pointer hover:bg-gray-100 border-b last:border-b-0 transition-colors"
                                                    onClick={() => handleBusinessEntityClick(entity)}
                                                >
                                                    <div className="flex flex-col space-y-1">
                                                        <span className="text-xs sm:text-sm font-medium text-black truncate">{entity.label}</span>
                                                        <div className="text-xs text-gray-600 flex flex-col sm:flex-row sm:space-x-4 space-y-1 sm:space-y-0">
                                                            <span className="truncate">Status: {entity.info.status}</span>
                                                            <span className="truncate">NZBN: {entity.info.nzbn}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Selected Business Fields */}
                            {selectedBusinessEntity.info?.nzbn && (
                                <div className="grid grid-cols-1 gap-3 sm:gap-4 mt-3 sm:mt-4">
                                    <div>
                                        <Label htmlFor="companyName" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                            Business Name *
                                        </Label>
                                        <Input
                                            type="text"
                                            id="companyName"
                                            autoComplete="off"
                                            {...register("companyName")}
                                            className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                            placeholder="Business name"
                                        />
                                        {errors.companyName && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.companyName.message}</p>}
                                    </div>

                                    <div>
                                        <Label htmlFor="NZBN" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                            NZBN
                                        </Label>
                                        <Input
                                            type="text"
                                            autoComplete="off"
                                            {...register("NZBN")}
                                            className="w-full bg-gray-50 text-gray-600 text-xs sm:text-sm md:text-base border-gray-300"
                                            placeholder="NZBN number"
                                            readOnly
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="industry" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                            Industry *
                                        </Label>

                                        <Select
                                            onValueChange={(value) => setValue("industry", parseInt(value, 10))}>
                                            <SelectTrigger className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red h-9"
                                            >
                                                <SelectValue placeholder="Select Industry" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white text-black border-gray-300 max-h-60 overflow-y-auto">
                                                {industries.map((industry) => (
                                                    <SelectItem key={industry.key} value={industry.key.toString()}>{industry.value}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <input type="hidden" {...register("industry")} />
                                        {errors.industry && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.industry.message}</p>}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleFormReset}
                                        className="text-xs sm:text-sm text-harvey-red hover:text-harvey-red/80 underline text-left"
                                    >
                                        Clear details
                                    </button>
                                </div>
                            )}

                            {/* Manual Company Entry */}
                            {!selectedBusinessEntity.info?.nzbn && (
                                <div className="mt-3 sm:mt-4">
                                    <p className="text-xs sm:text-sm text-gray-700 mb-2 sm:mb-3">No NZBN? Please enter your business details below:</p>
                                    <div className="grid grid-cols-1 gap-3 sm:gap-4">
                                        <div>
                                            <Label htmlFor="manualCompanyName" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                                Business Name *
                                            </Label>
                                            <Input
                                                type="text"
                                                id="manualCompanyName"
                                                autoComplete="off"
                                                {...register("companyName")}
                                                className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                                placeholder="Your business name"
                                            />
                                            {errors.companyName && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.companyName.message}</p>}
                                        </div>

                                        <div>
                                            <Label htmlFor="manualIndustry" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                                Industry *
                                            </Label>
                                            <Select
                                                onValueChange={(value) => setValue("industry", parseInt(value, 10))}>
                                                <SelectTrigger className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red h-9">
                                                    <SelectValue placeholder="Select Industry" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-white text-black border-gray-300 max-h-60 overflow-y-auto ">
                                                    {industries.map((industry) => (
                                                        <SelectItem key={industry.key} value={industry.key.toString()}>{industry.value}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <input type="hidden" {...register("industry")} />
                                            {errors.industry && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.industry.message}</p>}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Contact Person Section */}
                        <div className="border-b border-gray-200 pb-3 sm:pb-4">
                            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-black mb-2 sm:mb-3">Contact Person</h3>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div>
                                    <Label htmlFor="firstName" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                        First Name *
                                    </Label>
                                    <Input
                                        type="text"
                                        id="firstName"
                                        autoComplete="off"
                                        {...register("firstName")}
                                        className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                        placeholder="First name"
                                    />
                                    {errors.firstName && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.firstName.message}</p>}
                                </div>

                                <div>
                                    <Label htmlFor="lastName" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                        Last Name *
                                    </Label>
                                    <Input
                                        type="text"
                                        autoComplete="off"
                                        {...register("lastName")}
                                        className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                        placeholder="Last name"
                                    />
                                    {errors.lastName && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.lastName.message}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
                                <div>
                                    <Label htmlFor="email" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                        Email Address *
                                    </Label>
                                    <Input
                                        type="email"
                                        id="email"
                                        autoComplete="off"
                                        {...register("email")}
                                        className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                        placeholder="your.email@company.com"
                                    />
                                    {errors.email && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.email.message}</p>}
                                </div>

                                <div>
                                    <Label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                        Phone Number *
                                    </Label>
                                    <Input
                                        type="tel"
                                        id="phone"
                                        autoComplete="off"
                                        {...register("phone")}
                                        className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                        placeholder="+64 21 xxx xxxx"
                                    />
                                    {errors.phone && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.phone.message}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">

                                <div className="hidden">
                                    <Label htmlFor="building" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                        Building/Unit
                                    </Label>
                                    <Input
                                        type="text"
                                        id="building"
                                        autoComplete="off"
                                        {...register("building")}
                                        className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                        placeholder="e.g. Apartment 5B"
                                    />
                                    {errors.building && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.building.message}</p>}
                                </div>
                                <div className="hidden">
                                    <Label htmlFor="street" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                        Street
                                    </Label>
                                    <Input
                                        type="text"
                                        id="street"
                                        autoComplete="off"
                                        {...register("street")}
                                        className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                        placeholder="e.g. Queen Street"
                                    />
                                    {errors.street && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.street.message}</p>}
                                </div>
                                <div className="hidden">
                                    <Label htmlFor="suburb" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                        Suburb
                                    </Label>
                                    <Input
                                        type="text"
                                        id="suburb"
                                        autoComplete="off"
                                        {...register("suburb")}
                                        className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                        placeholder="e.g. Ponsonby"
                                    />
                                    {errors.suburb && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.suburb.message}</p>}
                                </div>
                                <div>
                                    <Label htmlFor="city" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                        City
                                    </Label>
                                    <Select onValueChange={value => setValue("city", value)}>
                                        <SelectTrigger className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red h-9">
                                            <SelectValue placeholder="Select City" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white text-black border-gray-300 max-h-60 overflow-y-auto">
                                            {cities.map(city => (
                                                <SelectItem key={city.key} value={city.value}>{city.value}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <input type="hidden" {...register("city")} />
                                    {errors.city && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.city.message}</p>}
                                </div>
                                {/* <div>
                                    <Label htmlFor="city" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                        City *
                                    </Label>
                                    <Input
                                        type="text"
                                        id="city"
                                        autoComplete="off"
                                        {...register("city")}
                                        className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                        placeholder="e.g. Auckland"
                                    />
                                    {errors.city && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.city.message}</p>}
                                </div> */}

                                <div>
                                    <Label htmlFor="postalCode" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                        Postal Code *
                                    </Label>
                                    <Input
                                        type="text"
                                        id="postalCode"
                                        autoComplete="off"
                                        {...register("postalCode")}
                                        className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                        placeholder="e.g. 1010"
                                    />
                                    {errors.postalCode && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.postalCode.message}</p>}
                                </div>


                                <div className="hidden">
                                    <Label htmlFor="country" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                        Country
                                    </Label>
                                    <Input
                                        type="text"
                                        id="country"
                                        readOnly
                                        autoComplete="off"
                                        {...register("country", { value: "New Zealand" })}
                                        className="w-full bg-gray-50 text-gray-600 text-xs sm:text-sm md:text-base border-gray-300"
                                        value="New Zealand"
                                    />
                                </div>
                            </div>

                        </div>

                        {/* Subject Section */}
                        <div className="pb-3 sm:pb-4">
                            <Label htmlFor="subject" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                Subject *
                            </Label>
                            <Input
                                type="text"
                                id="subject"
                                autoComplete="off"
                                maxLength={100}
                                {...register("subject")}
                                className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red"
                                placeholder="Brief description of your inquiry..."
                            />
                            {errors.subject && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.subject.message}</p>}
                        </div>

                        {/* Message Section */}
                        <div className="pb-3 sm:pb-4">
                            <Label htmlFor="message" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                Message *
                            </Label>
                            <Textarea
                                id="message"
                                rows={5}
                                {...register("message")}
                                className="w-full bg-white text-black text-xs sm:text-sm md:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-harvey-red resize-none sm:rows-4"
                                placeholder="Tell us about your requirements..."
                            />
                            {errors.message && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.message.message}</p>}
                        </div>

                        {/* File Upload Section */}
                        <div className="pb-3 sm:pb-4">
                            <Label htmlFor="files" className="block text-xs sm:text-sm font-medium text-black mb-1">
                                Attachments (Optional)
                            </Label>
                            <p className="text-xs text-gray-700 mb-2">Upload up to 3 files (max 10MB each)</p>

                            <div className="relative flex items-center">
                                <Input
                                    type="file"
                                    id="files"
                                    multiple
                                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.zip,.rar"
                                    onChange={handleFileChange}
                                    className="absolute left-0 top-0 w-0 h-0 opacity-0"
                                />
                                <Label
                                    htmlFor="files"
                                    className="btn inline-flex items-center justify-center h-8 min-w-[90px] cursor-pointer bg-harvey-red hover:bg-harvey-red/90 text-white text-xs sm:text-sm md:text-base font-medium rounded-md px-3 mr-4 transition-colors duration-150"
                                >
                                    Choose Files
                                </Label>
                                <span className="text-xs text-black truncate">
                                    {selectedFiles.length > 0
                                        ? selectedFiles.map((file) => file.name).join(', ')
                                        : "No files selected"}
                                </span>
                            </div>

                            {/* Display selected files */}
                            {selectedFiles.length > 0 && (
                                <div className="mt-2 space-y-2">
                                    {selectedFiles.map((file, index) => (
                                        <div key={index} className="flex items-center justify-between bg-gray-100 rounded-md p-2">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-700 truncate max-w-xs">{file.name}</span>
                                                <span className="text-xs text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeFile(index)}
                                                className="text-red-500 hover:text-red-700 text-xs font-medium"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {errors.files && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.files.message}</p>}
                        </div>

                        <Button
                            type="submit"
                            disabled={!isValid || submitting}  //added spinning and disable button if required field not filled out
                            className="w-full bg-harvey-red hover:bg-harvey-red/90 text-white py-2 sm:py-3 text-xs sm:text-sm md:text-base font-medium btn"
                        >
                            {/* Send Message */}
                            {submitting ? (
                                <>
                                    <LoadingSpinner size="sm" message="" fullScreen={false} inline />
                                    <span className="ml-2">Sending...</span>
                                </>
                            ) : (
                                "Send Message"
                            )}
                        </Button>
                    </form>
                )}
            </SheetContent>
        </Sheet>
    );
}
